import type {
  AttachmentResponse,
  DeviceRequest,
  InspectionDeviceAttachmentRequest,
  InspectionDeviceEntryResponse,
  InspectionDeviceRecordRequest,
  InspectionScopeDeviceResponse,
} from 'src/api/ProactApiClient';
import {
  equipmentTypeApiClient,
  inspectionApiClient,
  siteApiClient,
} from 'src/api/ProactApiClientInstances';
import {
  applyAddCustomDevice,
  applyRecordDevice,
} from 'src/app/inspections/offline/appliers';
import {INSPECTION_OUTBOX_KINDS} from 'src/app/inspections/offline/executors';
import type {InspectionCustomDeviceOptions} from 'src/app/inspections/offline/reads';
import {
  inspectionCustomDeviceOptionsQueryFn,
  inspectionDeviceEntryQueryFn,
  inspectionScopeDevicesQueryFn,
} from 'src/app/inspections/offline/reads';
import {inspectionContextKey} from 'src/app/inspections/offline/snapshotKeys';
import {offlineMutation} from 'src/offline/offlineMutation';
import {emptySplitApi} from '../api';
import {clientQuery} from '../clientQuery';

// Device-scoped recording-flow endpoints; header/list/assign live in
// `inspection.ts`, deficiency endpoints in `deficiency.ts`.

export const FULL_OPTIONS_REQUEST = {
  start: 0,
  length: 10000,
  filters: [],
  filterFns: [],
  sorting: [],
};

// Newtonsoft sends explicit `null`, generated client types `| undefined` —
// normalize once at the RTK Query boundary rather than null-safing every call site.
const normalizeScopeDevice = (
  device: InspectionScopeDeviceResponse
): InspectionScopeDeviceResponse => ({
  ...device,
  complianceState: device.complianceState ?? undefined,
  inspectionDeviceId: device.inspectionDeviceId ?? undefined,
});

const normalizeDeviceEntry = (
  entry: InspectionDeviceEntryResponse
): InspectionDeviceEntryResponse => ({
  ...entry,
  inspectionDeviceId: entry.inspectionDeviceId ?? undefined,
  complianceState: entry.complianceState ?? undefined,
  inspectedAt: entry.inspectedAt ?? undefined,
  fieldFormDefinitionId: entry.fieldFormDefinitionId ?? undefined,
  effectiveVersion: entry.effectiveVersion ?? undefined,
});

export const inspectionRecordApi = emptySplitApi.injectEndpoints({
  endpoints: builder => ({
    getInspectionScopeDevices: builder.query<
      InspectionScopeDeviceResponse[],
      number
    >({
      queryFn: inspectionScopeDevicesQueryFn(async id => {
        const devices = await inspectionApiClient.getDevices(id);
        return devices.map(normalizeScopeDevice);
      }),
      providesTags: ['InspectionDevices'],
    }),
    getInspectionCustomDeviceOptions: builder.query<
      InspectionCustomDeviceOptions,
      number
    >({
      queryFn: inspectionCustomDeviceOptionsQueryFn(async () => {
        const [sites, equipmentTypes] = await Promise.all([
          siteApiClient.listSelectOptions(FULL_OPTIONS_REQUEST),
          equipmentTypeApiClient.listSelectOptions(FULL_OPTIONS_REQUEST),
        ]);
        return {
          sites: sites.data ?? [],
          equipmentTypes: equipmentTypes.data ?? [],
        };
      }),
      providesTags: ['Sites', 'EquipmentTypes'],
    }),
    getInspectionDeviceEntry: builder.query<
      InspectionDeviceEntryResponse,
      {inspectionId: number; deviceId: number}
    >({
      queryFn: inspectionDeviceEntryQueryFn(async ({inspectionId, deviceId}) =>
        normalizeDeviceEntry(
          await inspectionApiClient.getDeviceEntry(inspectionId, deviceId)
        )
      ),
      providesTags: ['InspectionDevices'],
    }),
    recordInspectionDevice: builder.mutation<
      InspectionDeviceEntryResponse,
      {
        inspectionId: number;
        deviceId: number;
        request: InspectionDeviceRecordRequest;
      }
    >({
      queryFn: offlineMutation(
        INSPECTION_OUTBOX_KINDS.recordDevice,
        async ({inspectionId, deviceId, request}) =>
          normalizeDeviceEntry(
            await inspectionApiClient.recordDevice(
              inspectionId,
              deviceId,
              request
            )
          ),
        {
          contextKey: ({inspectionId}) => inspectionContextKey(inspectionId),
          applyLocal: applyRecordDevice,
        }
      ),
      invalidatesTags: ['InspectionDevices', 'Inspections'],
    }),
    addCustomInspectionDevice: builder.mutation<
      InspectionDeviceEntryResponse,
      {
        inspectionId: number;
        request: DeviceRequest;
        tempDeviceId: number;
        equipmentTypeName?: string;
      }
    >({
      queryFn: offlineMutation(
        INSPECTION_OUTBOX_KINDS.addCustomDevice,
        async ({inspectionId, request}) =>
          normalizeDeviceEntry(
            await inspectionApiClient.addCustomDevice(inspectionId, request)
          ),
        {
          contextKey: ({inspectionId}) => inspectionContextKey(inspectionId),
          applyLocal: (args, ctx) =>
            applyAddCustomDevice(
              {
                inspectionId: args.inspectionId,
                tempDeviceId: args.tempDeviceId,
                request: args.request,
                equipmentTypeName: args.equipmentTypeName,
              },
              ctx
            ),
        }
      ),
      invalidatesTags: ['InspectionDevices', 'Inspections'],
    }),
    addInspectionDeviceAttachment: builder.mutation<
      AttachmentResponse,
      {
        inspectionId: number;
        deviceId: number;
        request: InspectionDeviceAttachmentRequest;
      }
    >({
      queryFn: clientQuery(({inspectionId, deviceId, request}) =>
        inspectionApiClient.addDeviceAttachment(inspectionId, deviceId, request)
      ),
      invalidatesTags: ['InspectionDevices'],
    }),
  }),
});

export const {
  useGetInspectionScopeDevicesQuery,
  useLazyGetInspectionScopeDevicesQuery,
  useGetInspectionCustomDeviceOptionsQuery,
  useGetInspectionDeviceEntryQuery,
  useRecordInspectionDeviceMutation,
  useAddCustomInspectionDeviceMutation,
  useAddInspectionDeviceAttachmentMutation,
} = inspectionRecordApi;
